import { streamSse } from '@recipes/api-client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { authStore } from '@/lib/auth/auth-store';

export type SseStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

export interface UseSseOptions<T> {
  /** Called for every parsed `data:` frame. */
  onEvent: (event: T) => void;
  /** Return true when the stream is logically complete and must not reconnect. */
  isTerminal?: (event: T) => boolean;
  enabled?: boolean;
  /** Reconnect attempts before giving up (status becomes "error"). */
  maxRetries?: number;
  onError?: (error: unknown) => void;
}

export interface UseSseResult {
  status: SseStatus;
  /** Number of reconnect attempts so far. */
  attempts: number;
  /** Manually restart the stream (resets the retry counter). */
  reconnect: () => void;
}

/**
 * React binding over `streamSse`: opens the stream while mounted, reconnects with an
 * exponential backoff on network errors, aborts on unmount / url change. When the retry
 * budget is exhausted the status becomes "error" so callers can fall back to polling.
 */
export function useSse<T>(url: string | null, options: UseSseOptions<T>): UseSseResult {
  const { enabled = true, maxRetries = 3 } = options;
  const [status, setStatus] = useState<SseStatus>('idle');
  const [attempts, setAttempts] = useState(0);
  const [generation, setGeneration] = useState(0);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!url || !enabled) {
      setStatus('idle');
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    let attempt = 0;
    setAttempts(0);

    const run = async () => {
      while (!cancelled) {
        setStatus('connecting');
        let terminal = false;
        let receivedAny = false;
        try {
          const stream = streamSse<T>(url, {
            getAccessToken: () => authStore.getState().accessToken,
            signal: controller.signal,
          });
          for await (const event of stream) {
            if (cancelled) break;
            if (!receivedAny) {
              receivedAny = true;
              setStatus('open');
            }
            optionsRef.current.onEvent(event);
            if (optionsRef.current.isTerminal?.(event)) {
              terminal = true;
              break;
            }
          }
          if (cancelled) return;
          if (terminal) {
            setStatus('closed');
            return;
          }
          // Stream ended without a terminal event: treat as a dropped connection.
          if (!receivedAny) throw new Error('SSE stream ended without data');
          setStatus('closed');
          return;
        } catch (error) {
          if (cancelled || controller.signal.aborted) return;
          optionsRef.current.onError?.(error);
          attempt += 1;
          setAttempts(attempt);
          if (attempt > maxRetries) {
            setStatus('error');
            return;
          }
          const delay = Math.min(8000, 500 * 2 ** attempt);
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    };
    void run();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [url, enabled, maxRetries, generation]);

  const reconnect = useCallback(() => setGeneration((g) => g + 1), []);
  return { status, attempts, reconnect };
}
